import React, { useState, useEffect } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, useStripe, useElements, CardElement } from '@stripe/react-stripe-js';
import { Button } from '../shadcn/button';
import { Card, CardContent, CardHeader, CardTitle } from '../shadcn/card';
import { toast } from '../shadcn/sonner-toast';
import { CreditCard, Loader2, AlertCircle } from 'lucide-react';
import { formatCurrency } from './utils';
import apiClient from '../../lib/api';
import { createStripePromise } from '../../utils/stripe-utils';
import { useAuth } from '../../contexts/AuthContext';

// Initialize Stripe with publishable key
const stripePromise = createStripePromise(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

// Component that handles payment form
const PaymentFormComponent = ({
  videoGroups,
  calculateTotalDuration,
  calculateGroupDuration,
  calculateProcessingCost,
  videoDurations,
  onPaymentComplete,
  uploading,
  durationsLoading
}) => {
  const { currentUser } = useAuth();

  // Check if Stripe is loaded properly
  useEffect(() => {
    if (!process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY) {
      console.warn('Stripe publishable key is not set in environment variables');
      toast.error('Payment system not configured', {
        description: 'Contact support: Stripe integration is not properly configured.'
      });
    }
  }, []);

  const stripe = useStripe();
  const elements = useElements();

  const [paymentLoading, setPaymentLoading] = useState(false);
  const [cardComplete, setCardComplete] = useState(false);

  const handlePayment = async (e) => {
    e.preventDefault();

    if (!stripe || !elements) {
      // Stripe.js hasn't loaded yet
      toast.error('Payment system is still loading. Please try again.');
      return;
    }

    const totalDuration = calculateTotalDuration();
    if (!totalDuration) {
      toast.error('Error calculating processing time. Please try again.');
      return;
    }

    const amount = calculateProcessingCost();

    if (amount <= 0) {
      toast.error('Invalid processing amount calculated.');
      return;
    }

    try {
      setPaymentLoading(true);

      // Check if user is admin - if so, skip payment processing
      if (currentUser?.isAdmin) {
        // For admin users, create a mock payment intent response
        const paymentIntentId = `pi_admin_${Date.now()}_${currentUser.id || 'admin'}`;
        const clientSecret = `pi_admin_secret_${Date.now()}_${currentUser.id || 'admin'}`;

        // Create invoice for admin user
        const invoiceResponse = await apiClient.post('/api/billing/create-invoice', {
          description: `Video processing for ${totalDuration} hours`,
          videoProcessingDetails: videoGroups.map(group => ({
            groupId: group.id,
            videoCount: group.files?.length || 0,
            duration: calculateGroupDuration(group),
            files: group.files?.map(file => ({
              id: file.id,
              name: file.file?.name,
              size: file.file?.size,
              duration: videoDurations[file.id] ? videoDurations[file.id] / 60 : 0 // duration in minutes
            })) || []
          })),
          amount: 0, // Admin processing is free
          paymentIntentId: paymentIntentId
        });

        // Update invoice status to 'paid' for admin
        try {
          await apiClient.put(`/api/billing/invoice/${paymentIntentId}/status`, {
            status: 'paid'
          });
        } catch (invoiceUpdateError) {
          // Don't fail the payment flow if invoice status update fails
          console.error('Error updating admin invoice status:', invoiceUpdateError);
        }

        toast.success('Payment processed successfully (admin free processing)', {
          description: `Admin processing completed without charge.`
        });

        onPaymentComplete();
      } else {
        // Create payment intent on the backend for non-admin users
        const paymentIntentResponse = await apiClient.post('/api/billing/create-video-processing-payment-intent', {
          amount: amount,
          description: `Video processing for ${totalDuration} hours`,
          videoProcessingDetails: videoGroups.map(group => ({
            groupId: group.id,
            videoCount: group.files?.length || 0,
            duration: calculateGroupDuration(group)
          }))
        });

        const { clientSecret, paymentIntentId } = paymentIntentResponse.data;

        if (!clientSecret) {
          throw new Error('Failed to get payment intent client secret from server');
        }

        // Confirm the payment with the card details
        const cardElement = elements.getElement(CardElement);
        if (!cardElement) {
          toast.error('Card details not found. Please try again.');
          return;
        }

        const result = await stripe.confirmCardPayment(clientSecret, {
          payment_method: {
            card: cardElement,
            billing_details: {
              name: 'Customer', // This could be populated from user profile
            },
          }
        });

        if (result.error) {
          // Show error to customer
          console.error('Payment confirmation error:', result.error);
          toast.error('Payment failed', {
            description: result.error.message || 'An error occurred during payment processing.'
          });
        } else {
          // The payment has been processed!
          if (result.paymentIntent.status === 'succeeded') {
            // Create invoice after payment confirmation
            const invoiceResponse = await apiClient.post('/api/billing/create-invoice', {
              description: `Video processing for ${totalDuration} hours`,
              videoProcessingDetails: videoGroups.map(group => ({
                groupId: group.id,
                videoCount: group.files?.length || 0,
                duration: calculateGroupDuration(group),
                files: group.files?.map(file => ({
                  id: file.id,
                  name: file.file?.name,
                  size: file.file?.size,
                  duration: videoDurations[file.id] ? videoDurations[file.id] / 60 : 0 // duration in minutes
                })) || []
              })),
              amount: amount,
              paymentIntentId: paymentIntentId
            });



            // Update invoice status to 'paid' since payment was confirmed
            try {
              await apiClient.put(`/api/billing/invoice/${paymentIntentId}/status`, {
                status: 'paid'
              });
            } catch (invoiceUpdateError) {
              // Don't fail the payment flow if invoice status update fails
              // The webhook should eventually update the status from Stripe
            }

            toast.success('Payment confirmed successfully', {
              description: `Your payment of ${formatCurrency(amount)} has been confirmed.`
            });

            onPaymentComplete();
          } else {
            toast.error('Payment not completed', {
              description: 'Payment status is not succeeded. Please try again.'
            });
          }
        }
      }
    } catch (error) {
      console.error('Payment error:', error);
      toast.error('Payment failed', {
        description: error.message || 'An error occurred during payment processing.'
      });
    } finally {
      setPaymentLoading(false);
    }
  };

  const handleCardChange = (event) => {
    setCardComplete(event.complete);
  };

  return (
    <div>
      <Card className="mb-4">
        <CardHeader>
          <CardTitle className="flex items-center">
            <CreditCard className="h-5 w-5 mr-2 text-green-500" />
            Payment Information
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Card Details
            </label>
            <div className="border rounded p-3 bg-white shadow-sm focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500">
              <CardElement 
                options={{
                  style: {
                    base: {
                      fontSize: '16px',
                      color: '#424770',
                      '::placeholder': {
                        color: '#aab7c4',
                      },
                    },
                    invalid: {
                      color: '#9e2146',
                    },
                  },
                  hidePostalCode: true,
                }}
                onChange={handleCardChange}
              />
            </div>
            <p className="mt-2 text-xs text-gray-500">
              Securely enter your card details. Your information is encrypted and never stored.
            </p>
          </div>
          
          <Button 
            type="button"
            onClick={handlePayment}
            disabled={!stripe || !cardComplete || paymentLoading || uploading || durationsLoading}
            className="w-full transition-all duration-300 hover:scale-105 bg-gradient-to-r from-green-500 to-teal-600 hover:from-green-600 hover:to-teal-700"
          >
            {paymentLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Processing...
              </>
            ) : (
              <>
                <CreditCard className="mr-2 h-4 w-4" />
                {currentUser?.isAdmin ? 'Processing as Admin' : `Pay $${calculateProcessingCost().toFixed(2)}`}
              </>
            )}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

// Wrapper component that provides the Elements context
const StripePaymentForm = ({
  videoGroups,
  calculateTotalDuration,
  calculateGroupDuration,
  calculateProcessingCost,
  videoDurations,
  onPaymentComplete,
  uploading,
  durationsLoading
}) => {
  const [stripeReady, setStripeReady] = useState(false);
  const [stripeError, setStripeError] = useState(false);

  useEffect(() => {
    // Check if stripePromise resolves successfully
    stripePromise.then((stripe) => {
      if (stripe) {
        setStripeReady(true);
      } else {
        setStripeError(true);
        console.error('Failed to initialize Stripe. Check your publishable key.');
        toast.error('Payment system initialization failed. Please contact support.');
      }
    }).catch((error) => {
      setStripeError(true);
      console.error('Error initializing Stripe:', error);
      toast.error('Payment system initialization failed. Please contact support.');
    });
  }, []);

  if (stripeError) {
    return (
      <div className="text-center p-6 bg-red-50 rounded-lg border border-red-200">
        <h3 className="text-lg font-medium text-red-800 mb-2">Payment System Error</h3>
        <p className="text-red-700 mb-4">
          There was an error initializing the payment system. Please contact support.
        </p>
      </div>
    );
  }

  return (
    <div>
      {stripeReady ? (
        <Elements stripe={stripePromise}>
          <PaymentFormComponent
            videoGroups={videoGroups}
            calculateTotalDuration={calculateTotalDuration}
            calculateGroupDuration={calculateGroupDuration}
            calculateProcessingCost={calculateProcessingCost}
            videoDurations={videoDurations}
            onPaymentComplete={onPaymentComplete}
            uploading={uploading}
            durationsLoading={durationsLoading}
          />
        </Elements>
      ) : (
        <div className="text-center p-6 bg-blue-50 rounded-lg">
          <h3 className="text-lg font-medium text-blue-800 mb-2">Loading Payment System...</h3>
          <p className="text-blue-700">Please wait while we initialize the payment system.</p>
        </div>
      )}
    </div>
  );
};

export default StripePaymentForm;